'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { geoBeanchLocation } from '@/libs/haversine'

interface UsePromotionPopupOptions {
  branches: IBranch[]
  promotions: IPromotion[]
  locale: string
  delayMs?: number
  currentBranchPublicPath?: string
  currentCitySlug?: string
}

export function usePromotionPopup({
  branches,
  promotions,
  locale,
  delayMs = 500,
  currentBranchPublicPath,
  currentCitySlug
}: UsePromotionPopupOptions) {
  const [isVisible, setIsVisible] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [latestPromotion, setLatestPromotion] = useState<IPromotion | null>(null)
  const [nearestBranch, setNearestBranch] = useState<Branch | null>(null)
  const [hasShown, setHasShown] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Refs for cleanup
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Memoize flattened branches to avoid recalculation
  const allBranches = useMemo(() => branches.flatMap((branch) => branch.branches), [branches])

  const getCurrentBranchFromPublicPath = useCallback(() => {
    if (!currentBranchPublicPath) return null

    return (
      allBranches.find(
        (branch) => branch.public_path?.toLowerCase() === currentBranchPublicPath.toLowerCase()
      ) || null
    )
  }, [allBranches, currentBranchPublicPath])

  // Get branches from current city page
  const getCurrentCityBranches = useCallback(() => {
    if (!currentCitySlug) return []

    return allBranches.filter(
      (branch) => (branch as any).city_slug?.toLowerCase() === currentCitySlug.toLowerCase()
    )
  }, [allBranches, currentCitySlug])

  // Utility function to normalize branch ID for consistent comparison
  const normalizeBranchId = useCallback((branchId: string | number): string => {
    return typeof branchId === 'string' ? branchId : branchId.toString()
  }, [])

  // Check if promotion belongs to a specific branch
  const doesPromotionBelongToBranch = useCallback(
    (promotion: IPromotion, targetBranchId: string): boolean => {
      if (!promotion.PM_brachID) return false

      const normalizedPromoBranchId = normalizeBranchId(promotion.PM_brachID)

      // Split by comma and check exact matches to avoid partial matching issues
      const branchIds = normalizedPromoBranchId.split(',').map((id) => id.trim())
      return branchIds.includes(targetBranchId)
    },
    [normalizeBranchId]
  )

  const getPromotionByPriorityForBranch = useCallback(
    (branchId: string) => {
      // Get promotions that belong to the specified branch
      const matchingPromotions = promotions.filter((promo) =>
        doesPromotionBelongToBranch(promo, branchId)
      )

      if (matchingPromotions.length === 0) {
        return null
      }

      // Filter promotions that have promotion_popup_priority data
      const promotionsWithPriority = matchingPromotions.filter(
        (promo) =>
          (promo as any).promotion_popup_priority &&
          Array.isArray((promo as any).promotion_popup_priority) &&
          (promo as any).promotion_popup_priority.length > 0
      )

      if (promotionsWithPriority.length === 0) {
        const sortedByPosition = matchingPromotions.sort((a, b) => {
          const aPosition = (a as any).promotion_position || 999
          const bPosition = (b as any).promotion_position || 999
          return aPosition - bPosition // Lower position = higher priority
        })

        return sortedByPosition.length > 0 ? sortedByPosition[0] : null
      }

      // Find promotions that have priority data for the target branch
      const promotionsForBranch = promotionsWithPriority.filter((promo) => {
        return (promo as any).promotion_popup_priority.some(
          (priority: any) => priority.branch && priority.branch.toString() === branchId
        )
      })

      if (promotionsForBranch.length === 0) {
        const sortedByPosition = matchingPromotions.sort((a, b) => {
          const aPosition = (a as any).promotion_position || 999
          const bPosition = (b as any).promotion_position || 999
          return aPosition - bPosition // Lower position = higher priority
        })

        return sortedByPosition.length > 0 ? sortedByPosition[0] : null
      }

      const sortedByPriority = promotionsForBranch.sort((a, b) => {
        const aPriority =
          (a as any).promotion_popup_priority.find(
            (p: any) => p.branch && p.branch.toString() === branchId
          )?.priority || 999

        const bPriority =
          (b as any).promotion_popup_priority.find(
            (p: any) => p.branch && p.branch.toString() === branchId
          )?.priority || 999

        return aPriority - bPriority // Lower number = higher priority
      })

      return sortedByPriority[0]
    },
    [promotions, doesPromotionBelongToBranch]
  )

  const getPromotionsByPriorityForBranch = useCallback(
    (branchId: string): IPromotion[] => {
      // Get promotions that belong to the specified branch
      const matchingPromotions = promotions.filter((promo) =>
        doesPromotionBelongToBranch(promo, branchId)
      )

      if (matchingPromotions.length === 0) {
        return []
      }

      const sortedByPromotionId = matchingPromotions.sort((a, b) => b.promotion_id - a.promotion_id)

      return sortedByPromotionId
    },
    [promotions, doesPromotionBelongToBranch]
  )

  const getPromotionForCityPage = useCallback(async () => {
    if (!currentCitySlug) return null

    const cityBranches = getCurrentCityBranches()
    if (cityBranches.length === 0) return null

    const sortedBranches = cityBranches.sort((a, b) => Number(b.id) - Number(a.id))

    try {
      const nearestBranchInCity = await geoBeanchLocation(cityBranches)
      if (nearestBranchInCity) {
        setNearestBranch(nearestBranchInCity)
        const promotionForNearestBranch = getPromotionByPriorityForBranch(nearestBranchInCity.id)
        if (promotionForNearestBranch) {
          return promotionForNearestBranch
        }
      }
    } catch (error) {
      // console.error('Error detecting nearest branch in city:', error)
    }

    for (const branch of sortedBranches) {
      const promotionWithPriority = getPromotionByPriorityForBranch(branch.id)
      if (promotionWithPriority) {
        setNearestBranch(branch)
        return promotionWithPriority
      }

      const branchPromotions = promotions.filter((promo) =>
        doesPromotionBelongToBranch(promo, branch.id)
      )

      if (branchPromotions.length > 0) {
        const sortedByPosition = branchPromotions.sort((a, b) => {
          const aPosition = (a as any).promotion_position || 999
          const bPosition = (b as any).promotion_position || 999
          return aPosition - bPosition // Lower position = higher priority
        })

        if (sortedByPosition[0]) {
          setNearestBranch(branch)
          return sortedByPosition[0]
        }
      }
    }

    return null
  }, [
    currentCitySlug,
    getCurrentCityBranches,
    getPromotionByPriorityForBranch,
    doesPromotionBelongToBranch,
    promotions
  ])

  const getMaxIdPromotion = useCallback(
    (branchId?: string) => {
      if (!branchId || allBranches.length === 0) {
        // default promotion to show when no branch is specified
        const generalPriorityPromotions = promotions.filter((promo) => {
          return (
            (promo as any).promotion_popup_priority &&
            Array.isArray((promo as any).promotion_popup_priority) &&
            (promo as any).promotion_popup_priority.some(
              (priority: any) => priority.branch !== undefined && priority.branch.toString() === '0'
            )
          )
        })

        if (generalPriorityPromotions.length > 0) {
          const sortedByPriority = generalPriorityPromotions.sort((a, b) => {
            const aPriority =
              (a as any).promotion_popup_priority.find(
                (p: any) => p.branch !== undefined && p.branch.toString() === '0'
              )?.priority || 999

            const bPriority =
              (b as any).promotion_popup_priority.find(
                (p: any) => p.branch !== undefined && p.branch.toString() === '0'
              )?.priority || 999

            return aPriority - bPriority // Lower number = higher priority
          })

          return sortedByPriority[0]
        }

        if (promotions.length > 0) {
          const sortedByPosition = promotions.sort((a, b) => {
            const aPosition = (a as any).promotion_position || 999
            const bPosition = (b as any).promotion_position || 999
            return aPosition - bPosition // Lower position = higher priority
          })

          return sortedByPosition[0]
        }

        return null
      }

      // Filter promotions for specific branch
      const branchPromotions = promotions.filter((promo) =>
        doesPromotionBelongToBranch(promo, branchId)
      )

      if (branchPromotions.length > 0) {
        // Sort by promotion_position (lowest first)
        const sortedByPosition = branchPromotions.sort((a, b) => {
          const aPosition = (a as any).promotion_position || 999
          const bPosition = (b as any).promotion_position || 999
          return aPosition - bPosition // Lower position = higher priority
        })

        return sortedByPosition[0]
      }

      return null
    },
    [promotions, allBranches.length, doesPromotionBelongToBranch]
  )

  const detectLocation = useCallback(async () => {
    setIsLoading(true)

    try {
      let targetBranch = null

      if (currentCitySlug && !currentBranchPublicPath) {
        const cityPromotion = await getPromotionForCityPage()
        if (cityPromotion) {
          setLatestPromotion(cityPromotion)
          return
        } else {
          return
        }
      }

      // Determine target branch based on URL or geolocation
      if (currentBranchPublicPath) {
        targetBranch = getCurrentBranchFromPublicPath()
        if (targetBranch) {
          setNearestBranch(targetBranch)

          const promotionForCurrentBranch = getPromotionByPriorityForBranch(targetBranch.id)
          if (promotionForCurrentBranch) {
            setLatestPromotion(promotionForCurrentBranch)
            return
          } else {
            return
          }
        } else {
          return
        }
      } else {
        // Not on branch detail page - use geolocation
        targetBranch = await geoBeanchLocation(allBranches)
        if (targetBranch) {
          setNearestBranch(targetBranch)
        }
      }

      if (targetBranch) {
        const promotionForBranch = getPromotionByPriorityForBranch(targetBranch.id)
        if (promotionForBranch) {
          setLatestPromotion(promotionForBranch)
          return
        }
      }

      // First check for promotions with promotion_popup_priority:branch=0
      const generalPriorityPromotions = promotions.filter((promo) => {
        return (
          (promo as any).promotion_popup_priority &&
          Array.isArray((promo as any).promotion_popup_priority) &&
          (promo as any).promotion_popup_priority.some(
            (priority: any) => priority.branch !== undefined && priority.branch.toString() === '0'
          )
        )
      })

      if (generalPriorityPromotions.length > 0) {
        // Sort by priority (1 is highest priority)
        const sortedByPriority = generalPriorityPromotions.sort((a, b) => {
          const aPriority =
            (a as any).promotion_popup_priority.find(
              (p: any) => p.branch !== undefined && p.branch.toString() === '0'
            )?.priority || 999

          const bPriority =
            (b as any).promotion_popup_priority.find(
              (p: any) => p.branch !== undefined && p.branch.toString() === '0'
            )?.priority || 999

          return aPriority - bPriority // Lower number = higher priority
        })

        setLatestPromotion(sortedByPriority[0])
        return
      }

      // Final fallback: Get promotion with lowest promotion_position from all promotions
      if (promotions.length > 0) {
        const sortedByPosition = promotions.sort((a, b) => {
          const aPosition = (a as any).promotion_position || 999
          const bPosition = (b as any).promotion_position || 999
          return aPosition - bPosition // Lower position = higher priority
        })

        setLatestPromotion(sortedByPosition[0])
      }
    } catch (error) {
      // Handle geolocation errors or other exceptions

      // First check for promotions with promotion_popup_priority:branch=0
      const generalPriorityPromotions = promotions.filter((promo) => {
        return (
          (promo as any).promotion_popup_priority &&
          Array.isArray((promo as any).promotion_popup_priority) &&
          (promo as any).promotion_popup_priority.some(
            (priority: any) => priority.branch !== undefined && priority.branch.toString() === '0'
          )
        )
      })

      if (generalPriorityPromotions.length > 0) {
        const sortedByPriority = generalPriorityPromotions.sort((a, b) => {
          const aPriority =
            (a as any).promotion_popup_priority.find(
              (p: any) => p.branch !== undefined && p.branch.toString() === '0'
            )?.priority || 999

          const bPriority =
            (b as any).promotion_popup_priority.find(
              (p: any) => p.branch !== undefined && p.branch.toString() === '0'
            )?.priority || 999

          return aPriority - bPriority // Lower number = higher priority
        })

        setLatestPromotion(sortedByPriority[0])
      } else if (promotions.length > 0) {
        const sortedByPosition = promotions.sort((a, b) => {
          const aPosition = (a as any).promotion_position || 999
          const bPosition = (b as any).promotion_position || 999
          return aPosition - bPosition // Lower position = higher priority
        })

        setLatestPromotion(sortedByPosition[0])
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    allBranches,
    getPromotionByPriorityForBranch,
    getPromotionsByPriorityForBranch,
    getMaxIdPromotion,
    currentBranchPublicPath,
    getCurrentBranchFromPublicPath,
    currentCitySlug,
    getPromotionForCityPage,
    promotions
  ])

  // Cleanup function
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  // Initialize popup with proper cleanup
  useEffect(() => {
    if (!promotions.length) {
      return
    }

    // Clear any existing timeout
    cleanup()

    timeoutRef.current = setTimeout(() => {
      detectLocation()
    }, delayMs)

    return cleanup
  }, [promotions.length, delayMs, detectLocation, cleanup])

  // Effect to show popup once we have processed location and promotion
  useEffect(() => {
    if (latestPromotion && !isLoading && !isVisible && !hasShown) {
      setIsVisible(true)
      setHasShown(true)
    }
  }, [latestPromotion, isLoading, isVisible, hasShown])

  // Cleanup on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])

  // Optimized close handler with proper cleanup
  const handleClose = useCallback(() => {
    setIsClosing(true)

    closeTimeoutRef.current = setTimeout(() => {
      setIsVisible(false)
      setIsClosing(false)
      // Keep hasShown as true so popup doesn't reappear until next page load
    }, 300)
  }, [])

  // Handle book now click เผื่อใช้
  const handleBookNow = useCallback(() => {
    // booking logic
  }, [latestPromotion])

  // Reset popup with proper cleanup
  const resetPopup = useCallback(() => {
    cleanup()
    setHasShown(false)
    setIsVisible(false)
    setIsClosing(false)
    setLatestPromotion(null)
    setNearestBranch(null)
  }, [cleanup])

  return {
    isVisible: isVisible && !!latestPromotion,
    isClosing,
    isLoading,
    latestPromotion,
    nearestBranch,
    hasShown,
    handleClose,
    handleBookNow,
    resetPopup
  }
}
